'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

type AuthMode = 'login' | 'signup';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const valueProps = [
  {
    id: 'vp-multiagent',
    icon: 'CpuChipIcon',
    iconColor: 'text-sky-400',
    title: 'Multi-Agent Consensus Engine',
    desc: 'OpenAI, Anthropic, Google & DeepSeek audit your contracts in parallel - then reach consensus.',
  },
  {
    id: 'vp-audit',
    icon: 'ShieldCheckIcon',
    iconColor: 'text-amber-400',
    title: 'Automated Security Auditing',
    desc: 'Slither, Mythril & AI agents find vulnerabilities before they reach mainnet.',
  },
  {
    id: 'vp-deploy',
    icon: 'RocketLaunchIcon',
    iconColor: 'text-orange-400',
    title: 'One-Click Testnet Deployment',
    desc: 'Deploy to Sepolia, Base Goerli, Arbitrum testnet, or Solana Devnet in seconds.',
  },
  {
    id: 'vp-version',
    icon: 'ClockIcon',
    iconColor: 'text-cyan-400',
    title: 'Full Version History',
    desc: 'Every AI generation and manual edit creates an immutable snapshot. Compare diffs, restore anytime.',
  },
];

const ecosystems = [
  { id: 'eco-solidity', label: 'Solidity', color: 'text-[#a8c8e8]' },
  { id: 'eco-rust', label: 'Rust / Anchor', color: 'text-[#a8c8e8]' },
  { id: 'eco-evm', label: 'EVM Chains', color: 'text-[#a8c8e8]' },
  { id: 'eco-solana', label: 'Solana', color: 'text-[#a8c8e8]' },
];

const trustSignals = [
  { id: 'ts-tls', icon: 'LockClosedIcon', label: 'TLS 1.3 Encrypted', color: 'text-amber-400' },
  { id: 'ts-sandbox', icon: 'CubeIcon', label: 'Sandboxed Execution', color: 'text-sky-400' },
  { id: 'ts-soc', icon: 'DocumentCheckIcon', label: 'SOC 2 Compliant', color: 'text-emerald-400' },
];

export default function AuthPage() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp, signInWithGoogle, signInWithGitHub } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeTerms: false },
  });

  const handleLogin = loginForm.handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in successfully');
      router.push('/project-workspace');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Invalid credentials. Check email and password.');
    } finally {
      setIsLoading(false);
    }
  });

  const handleSignup = signupForm.handleSubmit(async (data) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      toast.success('Account created - welcome to Smartcontrax!');
      router.push('/project-workspace');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  });

  const handleOAuth = async (provider: 'google' | 'github') => {
    try {
      if (provider === 'google') {
        await signInWithGoogle();
      } else {
        await signInWithGitHub();
      }
    } catch (error: any) {
      toast.error(error?.message || `Failed to sign in with ${provider}`);
    }
  };

  return (
    <div className="min-h-screen flex bg-black">
      {/* Left - Brand Panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col bg-black overflow-hidden border-r border-[#0b6600]">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-40" />

        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={36} />
            <span className="font-semibold text-lg text-white tracking-tight">Smartcontrax</span>
          </div>

          {/* Hero */}
          <div className="mb-10">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              The AI Smart Contract
              <br />
              <span className="text-[#39FF14]">Operating System</span>
            </h1>
            <p className="text-[#a8c8e8] text-base leading-relaxed max-w-md">
              Design, audit, test, and deploy production-grade smart contracts through multi-agent AI consensus verification.
            </p>
          </div>

          {/* Value props */}
          <div className="space-y-4 mb-10">
            {valueProps.map((vp) => (
              <div key={vp.id} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-black border border-[#0b6600] flex items-center justify-center shrink-0 mt-0.5">
                  <Icon name={vp.icon as Parameters<typeof Icon>[0]['name']} size={16} className={vp.iconColor} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{vp.title}</p>
                  <p className="text-xs text-[#a8c8e8] leading-relaxed mt-0.5">{vp.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Ecosystems */}
          <div className="mb-8">
            <p className="text-xs text-[#a8c8e8] uppercase tracking-widest mb-3 font-medium">Supported Ecosystems</p>
            <div className="flex flex-wrap gap-2">
              {ecosystems.map((eco) => (
                <span
                  key={eco.id}
                  className={`text-xs font-medium px-2.5 py-1 rounded-lg bg-black border border-[#055400] ${eco.color}`}
                >
                  {eco.label}
                </span>
              ))}
            </div>
          </div>

          {/* Trust signals */}
          <div className="mt-auto flex items-center gap-5">
            {trustSignals.map((ts) => (
              <div key={ts.id} className="flex items-center gap-1.5 text-xs text-[#a8c8e8]">
                <Icon name={ts.icon as Parameters<typeof Icon>[0]['name']} size={13} className={ts.color} />
                {ts.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-black">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-semibold text-base text-white">Smartcontrax</span>
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 p-1 bg-black rounded-xl mb-8 border border-[#055400]">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-500 ${
                mode === 'login' ?'bg-black border border-[#0b6600] text-[#39FF14]' :'text-[#a8c8e8] hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-500 ${
                mode === 'signup' ?'bg-black border border-[#0b6600] text-[#39FF14]' :'text-[#a8c8e8] hover:text-white'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* OAuth */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => handleOAuth('google')}
              className="auth-btn-secondary justify-center py-2.5"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
            <button
              onClick={() => handleOAuth('github')}
              className="auth-btn-secondary justify-center py-2.5"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[#a8c8e8]">
                <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
              </svg>
              Continue with GitHub
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[#055400]" />
            <span className="text-xs text-[#a8c8e8]">or continue with email</span>
            <div className="flex-1 h-px bg-[#055400]" />
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4 animate-fade-in">
              <div>
                <label className="block text-sm font-medium text-white mb-1.5" htmlFor="login-email">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="input-field"
                  placeholder="you@company.com"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email address' },
                  })}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-400 mt-1.5">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-white" htmlFor="login-password">
                    Password
                  </label>
                  <button type="button" className="text-xs text-[#a8c8e8] hover:text-white transition-colors duration-300">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    placeholder="••••••••"
                    {...loginForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8c8e8] hover:text-white transition-colors duration-300"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-400 mt-1.5">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#055400] bg-black"
                  style={{ accentColor: '#39FF14' }}
                  {...loginForm.register('rememberMe')}
                />
                <label htmlFor="remember-me" className="text-sm text-[#a8c8e8] cursor-pointer">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="auth-btn-primary w-full justify-center py-2.5 text-sm font-semibold mt-2"
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Icon name="ArrowRightOnRectangleIcon" size={16} />
                    Sign In to Smartcontrax
                  </>
                )}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4 animate-fade-in">
              <div>
                <label className="block text-sm font-medium text-white mb-1.5" htmlFor="signup-name">
                  Full name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  className="input-field"
                  placeholder="Alex Kirilov"
                  {...signupForm.register('fullName', {
                    required: 'Full name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                />
                {signupForm.formState.errors.fullName && (
                  <p className="text-xs text-red-400 mt-1.5">{signupForm.formState.errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1.5" htmlFor="signup-email">
                  Work email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  className="input-field"
                  placeholder="you@company.com"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email address' },
                  })}
                />
                {signupForm.formState.errors.email && (
                  <p className="text-xs text-red-400 mt-1.5">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1.5" htmlFor="signup-password">
                  Password
                </label>
                <p className="text-xs text-[#a8c8e8] mb-1.5">Minimum 8 characters, include a number and symbol.</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    placeholder="Create a strong password"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Password must be at least 8 characters' },
                      pattern: {
                        value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
                        message: 'Include at least one number and one special character',
                      },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8c8e8] hover:text-white transition-colors duration-300"
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-red-400 mt-1.5">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1.5" htmlFor="signup-confirm">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="input-field pr-10"
                    placeholder="Repeat your password"
                    {...signupForm.register('confirmPassword', {
                      required: 'Please confirm your password',
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8c8e8] hover:text-white transition-colors duration-300"
                  >
                    <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-400 mt-1.5">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="agree-terms"
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#055400] bg-black mt-0.5"
                  style={{ accentColor: '#39FF14' }}
                  {...signupForm.register('agreeTerms', {
                    required: 'You must agree to the terms to continue',
                  })}
                />
                <label htmlFor="agree-terms" className="text-sm text-[#a8c8e8] cursor-pointer leading-relaxed">
                  I agree to the{' '}
                  <span className="text-[#0b6600] hover:text-[#39FF14] transition-colors duration-300 cursor-pointer">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-[#0b6600] hover:text-[#39FF14] transition-colors duration-300 cursor-pointer">Privacy Policy</span>
                </label>
              </div>
              {signupForm.formState.errors.agreeTerms && (
                <p className="text-xs text-red-400">{signupForm.formState.errors.agreeTerms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="auth-btn-primary w-full justify-center py-2.5 text-sm font-semibold mt-2"
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <Icon name="SparklesIcon" size={16} />
                    Start Building Smart Contracts
                  </>
                )}
              </button>

              <p className="text-xs text-[#a8c8e8] text-center">
                No wallet required to start. Connect later for deployment.
              </p>
            </form>
          )}

          {/* Plan teaser */}
          <div className="mt-6 p-4 rounded-xl bg-black border border-[#055400]">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="SparklesIcon" size={14} className="text-violet-400" />
              <p className="text-xs font-semibold text-white">Free plan includes:</p>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { id: 'fp-1', text: '3 active projects' },
                { id: 'fp-2', text: '500 AI credits/mo' },
                { id: 'fp-3', text: 'Audit reports' },
                { id: 'fp-4', text: 'Testnet deployment' },
              ].map((item) => (
                <div key={item.id} className="flex items-center gap-1.5 text-xs text-[#a8c8e8]">
                  <Icon name="CheckCircleIcon" size={12} className="text-emerald-400 shrink-0" />
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}